import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Flex } from 'antd';
import { useMemo } from 'react';
import { Padding } from '../../common/components';
import { Modal } from '../../common/components/modal/Modal';
import { ModalFooter } from '../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../common/components/modal/ModalTitle';
import { getMapBy } from '../../common/utils';
import { AlgorithmCollapsibleHeader } from '../algorithms/AlgorithmCollapsibleHeader';
import { AlgorithmInfo, RenderRow } from '../algorithms/AlgorithmInfo';
import { AlgorithmStatus } from '../algorithms/AlgorithmStatus';
import { AlgorithmExtendedType } from '../algorithms/schema/types';
import translations from './AlternativeAlgorithmViewModal.translations.json';
import { ViewableGroupOrAlgorithm } from './types';

const { addTranslations, t } = i18nNsFactory('alternatives-details-view');
addTranslations(translations);

export type AlternativeAlgorithmViewModalProps = {
  viewable: ViewableGroupOrAlgorithm;
  algorithms: AlgorithmExtendedType[];
  isEditMode: boolean;
  onClose: () => void;
};

export const AlternativeAlgorithmViewModal: React.FC<
  AlternativeAlgorithmViewModalProps
> = (props) => {
  const { viewable, algorithms, isEditMode, onClose } = props;
  const { code, groupCode, alternativeCode } = viewable;

  const sourcesById = useMemo(
    () => getMapBy(algorithms.flatMap((a) => a.sources).filter(Boolean), 'id'),
    [algorithms],
  );
  const mappingsById = useMemo(
    () => getMapBy(algorithms.flatMap((a) => a.mappings).filter(Boolean), 'id'),
    [algorithms],
  );

  const algorithm = useMemo(
    () => (code ? algorithms.find((a) => a.code === code) : undefined),
    [algorithms, code],
  );
  const groupAlgorithms = useMemo(
    () =>
      groupCode ? algorithms.filter((a) => a.groupCode === groupCode) : [],
    [algorithms, groupCode],
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Button
            id="close-alternative-algorithm-modal"
            type="primary"
            onClick={onClose}
          >
            {t('button.close')}
          </Button>
        </ModalFooter>
      }
      onCancel={onClose}
      width={800}
    >
      <ModalHeader>
        <Flex justify="space-between" style={{ paddingRight: 16 }}>
          <ModalTitle>{code ?? groupCode}</ModalTitle>
          {algorithm && <AlgorithmStatus statusId={algorithm.statusId} />}
        </Flex>
      </ModalHeader>
      <Padding scrollable>
        {algorithm && (
          <AlgorithmInfo
            algorithm={algorithm}
            sourcesById={sourcesById}
            mappingsById={mappingsById}
            alternativeCode={alternativeCode}
            showTargetEdit={isEditMode}
            renderRow={renderRow}
          />
        )}
        {groupAlgorithms.length > 0 && (
          <Flex vertical gap="middle">
            {groupAlgorithms.map((algorithm, index) => (
              <AlgorithmCollapsibleHeader
                key={index}
                algorithm={algorithm}
                sourcesById={sourcesById}
                mappingsById={mappingsById}
                alternativeCode={alternativeCode}
                isEditMode={isEditMode}
                renderRow={renderRow}
              />
            ))}
          </Flex>
        )}
      </Padding>
    </Modal>
  );
};

const renderRow: RenderRow = (key, _value, defaultNode) =>
  key === 'group' ? null : defaultNode;
